import { BrandId } from 'utils/override';
import { FormValidationRules } from '$/modules/form';

const PATTERN_STR_NO_EXCESS_SPACE_MAX_248 = /^\S{1}.{0,247}$/;
const PATTERN_STR_NO_EXCESS_SPACE_MAX_10 = /^\S{1}.{0,9}$/;
const PATTERN_STR_NO_EXCESS_SPACE_MAX_40 = /^\S{1}.{0,39}$/;
const PATTERN_STR_NO_TRAILING_SPACE_MAX_248 = /^\S(.*\S)?$/;
const PATTERN_NO_PREFIX_NUMBERS = /^(?!^[0-9].*$).*/;
const PATTERN_STR_NO_SUFFIX_NUMBERS = /^(?![ ]{1})/;
const PATTERN_STR_NO_DOUBLE_SPACE = /^([a-zA-Z0-9'.-]+\s)*[a-zA-Z0-9'.-]+$/;
const PATTERN_STR_NO_DOUBLE_SPACE_ALL = /^[^\s]*([\s]?[^\s]+)*$/;
const PATTERN_STR_NO_DOUBLE_SPACE_AND_LEADING_SPACE = /^(?!.* {2})\S(?:.*\S)?$/;
const PATTERN_INSURED_NAME = /^[a-zA-Z_ \-']*$/;
const PATTERN_ALPHA_NUMERIC_SPACE_ONLY = /^[a-zA-Z0-9_ ]+$/;
const PATTERN_ALPHA_SPACE_ONLY = /^[a-zA-Z ]+$/;
const PATTERN_ALPHA_NUMERIC_SPACE_COMMA_HYPHEN_ONLY = /^[a-zA-Z0-9_ ,-]+$/;
const PATTERN_CHAR_BEFORE_AND_SPACE_AFTER_COMMA =
  /^(?!.*\s,)(?!.*^,)(?!.*,\s{2,})(?!.*,[^\s])(?!.*,\s*$).*$/;
const PATTERN_ALPHA_NUMERIC = /^[a-zA-Z0-9]+$/;
const PATTERN_ALPHA_NUMERIC_HYPHEN_FWDSLASH_ONLY = /^[a-zA-Z0-9-/]+$/;
// Despite the name, the ` -/` fragment is a character RANGE (0x20-0x2F), so
// this also allows ! " # $ % & ' ( ) * + , - . — i.e. the punctuation real
// addresses and company names need ("Bank of China (Hong Kong) Limited",
// "Queen's Road", "Flat 5, 12/F"). Callers rely on that; do not "fix" the
// class into `[a-zA-Z0-9_\- /]`, which would reject those values.
const PATTERN_ALPHA_NUMERIC_HYPHEN_FWDSLASH_SPACE_ONLY = /^[a-zA-Z0-9_ -/]+$/;
const PATTERN_STR_NO_DOUBLE_SPACE_HYPHEN_FWDSLASH_SPACE_ONLY =
  /^([a-zA-Z0-9-/]+\s)*[a-zA-Z0-9-/]+$/;
const PATTERN_NO_BLANK_SPACE_BEFORE_AFTER_HYPHEN = /^(?!.*(?:\s-|-\s)).*$/;
const PATTERN_EQUAL_OR_LESS_THAN_3_CONSECUTIVE_CHAR = /^(?!.*?(.)\1{3}).*$/;
const PATTERN_1_TO_18_CHARS_ONLY = /^.{1,18}$/;
const PATTERN_1_TO_50_CHARS_ONLY = /^.{1,50}$/;
const PATTERN_2_TO_30_CHARS_ONLY = /^.{2,30}$/;
const PATTERN_1_TO_105_CHARS_ONLY = /^.{1,105}$/;
const PATTERN_PASSPORT = /^[a-zA-Z0-9]{6,12}$/;
const PATTERN_NRIC = /^[ST]\d{7}[A-Z]$/;
const PATTERN_FIN = /^[FGM]\d{7}[A-Z]$/;
const PATTERN_SG_ID = /^[STFGM]\d{7}[A-Za-z]$/;
const PATTERN_HK_ID = /^([A-Za-z]{1,2})([0-9]{6})(\(([Aa0-9])\)|[Aa0-9])$/;
const PATTERN_STR_EMAIL =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
const PATTERN_SG_PHONE_NUMBER = /^[89](?:\s*\d){7}$/;
const PATTERN_HK_MOBILE_NUMBER = /^[4-9](?:\s*\d){7}$/;
const PATTERN_MUST_BE_AT_LEAST_6_CHARS = /^.{6,}$/;
const PATTERN_MUST_BE_9_CHARS = /^.{9}$/;
const PATTERN_MUST_BE_8_DIGITS = /^(?:\d\s*){8}$/;
const PATTERN_MSIG_BUILDING_NAME = /^[-/'.(),+_=&@#$%*:;? a-zA-Z0-9]+$/;
const PATTERN_ONLY_ALLOW_INITIAL_ALPHANUMERIC = /^[a-zA-Z0-9].*$/;

export const getEmailValidationRules = ({
  errorMessage,
}: {
  errorMessage: {
    invalid: string;
  };
}): Array<FormValidationRules> => {
  return [{ pattern: PATTERN_STR_EMAIL, errorMessage: errorMessage.invalid }];
};

export const getInsuredNameMaxLength = ({
  brand,
  carrierCode,
  type,
}: {
  brand: string;
  carrierCode: string;
  type: 'firstName' | 'lastName';
}): number | undefined => {
  if (brand === 'singsaver') {
    if (carrierCode === 'SSHLAS') {
      return 50;
    }

    if (carrierCode === 'HLAS' && type === 'firstName') {
      return 20;
    }

    return 30;
  }

  if (brand === 'moneyhero') {
    if (carrierCode === 'QBE') {
      return 50;
    }

    return 30;
  }

  return undefined;
};

export const getInsuredNameValidationRules = ({
  errorMessage,
}: {
  errorMessage: {
    invalid: string;
    specialChar: string;
    spaces: string;
  };
}): Array<FormValidationRules> => [
  {
    pattern: PATTERN_ALPHA_SPACE_ONLY,
    errorMessage: errorMessage.specialChar,
  },
  {
    pattern: PATTERN_STR_NO_DOUBLE_SPACE_AND_LEADING_SPACE,
    errorMessage: errorMessage.spaces,
  },
  {
    pattern: PATTERN_STR_NO_SUFFIX_NUMBERS,
    errorMessage: errorMessage.invalid,
  },
  {
    pattern: PATTERN_INSURED_NAME,
    errorMessage: errorMessage.invalid,
  },
];

export const getPhoneNumberMaxLength = ({
  brand,
}: {
  brand: BrandId;
}): number | undefined => {
  if (brand === 'singsaver') {
    return 9;
  }

  if (brand === 'moneyhero') {
    return 9;
  }

  return undefined;
};

export const getPhoneNumberValidationRules = ({
  brand,
  errorMessage,
}: {
  brand: BrandId;
  errorMessage: {
    invalid: string;
    mustBe8Digits?: string;
    mustStartWith8or9?: string;
    mustStartWithMobile?: string;
  };
}): Array<FormValidationRules> => {
  if (brand === 'singsaver') {
    return [
      {
        pattern: PATTERN_MUST_BE_8_DIGITS,
        errorMessage: errorMessage?.mustBe8Digits || errorMessage?.invalid,
      },
      {
        pattern: PATTERN_SG_PHONE_NUMBER,
        errorMessage: errorMessage?.mustStartWith8or9 || errorMessage.invalid,
      },
    ];
  }

  if (brand === 'moneyhero') {
    return [
      {
        pattern: PATTERN_MUST_BE_8_DIGITS,
        errorMessage: errorMessage?.mustBe8Digits || errorMessage.invalid,
      },
      {
        pattern: PATTERN_HK_MOBILE_NUMBER,
        errorMessage: errorMessage?.mustStartWithMobile || errorMessage.invalid,
      },
    ];
  }

  return [];
};

export const getPostCodeValidationRules = ({
  brand,
  errorMessage,
}: {
  brand: BrandId;
  errorMessage: {
    invalid: string;
  };
}): Array<FormValidationRules> => {
  if (brand === 'singsaver') {
    return [
      {
        pattern: /^[0-9]{6}$/,
        errorMessage: errorMessage.invalid,
      },
    ];
  }

  if (brand === 'moneyhero') {
    return [
      {
        pattern: /^[0-9]{6,8}$/,
        errorMessage: errorMessage.invalid,
      },
    ];
  }

  return [];
};

export const getUnitNumberValidationRules = ({
  brand,
  errorMessage,
}: {
  brand: BrandId;
  errorMessage: {
    invalid: string;
  };
}): Array<FormValidationRules> => {
  if (brand === 'singsaver') {
    return [
      {
        pattern: /^[0-9]{1,3}-[a-zA-Z0-9]{1,6}$/,
        errorMessage: errorMessage.invalid,
      },
    ];
  }

  if (brand === 'moneyhero') {
    return [
      {
        pattern: PATTERN_1_TO_18_CHARS_ONLY,
        errorMessage: errorMessage.invalid,
      },
      {
        pattern: PATTERN_ALPHA_NUMERIC_HYPHEN_FWDSLASH_ONLY,
        errorMessage: errorMessage.invalid,
      },
    ];
  }

  return [];
};

export const getBlockValidationRules = ({
  brand,
  carrierCode,
  errorMessage,
}: {
  brand: BrandId;
  carrierCode: string;
  errorMessage: {
    invalid: string;
    maxLengthLessThanTen: string;
  };
}): Array<FormValidationRules> => {
  if (brand === 'singsaver') {
    return [
      {
        pattern: PATTERN_STR_NO_TRAILING_SPACE_MAX_248,
        errorMessage: errorMessage.invalid,
      },
      ...(carrierCode === 'MSIG'
        ? [
            {
              pattern: PATTERN_STR_NO_EXCESS_SPACE_MAX_10,
              errorMessage: errorMessage.maxLengthLessThanTen,
            },
            {
              pattern: PATTERN_ALPHA_NUMERIC,
              errorMessage: errorMessage.invalid,
            },
          ]
        : []),
    ];
  }

  if (brand === 'moneyhero') {
    return [
      {
        pattern: PATTERN_1_TO_18_CHARS_ONLY,
        errorMessage: errorMessage.invalid,
      },
      {
        pattern: PATTERN_ALPHA_NUMERIC_HYPHEN_FWDSLASH_ONLY,
        errorMessage: errorMessage.invalid,
      },
    ];
  }

  return [];
};

export const getStreetNumberValidationRules = ({
  errorMessage,
}: {
  errorMessage: {
    invalid: string;
  };
}): Array<FormValidationRules> => {
  return [
    {
      pattern: PATTERN_1_TO_18_CHARS_ONLY,
      errorMessage: errorMessage.invalid,
    },
    {
      pattern: PATTERN_ALPHA_NUMERIC_HYPHEN_FWDSLASH_ONLY,
      errorMessage: errorMessage.invalid,
    },
  ];
};

export const getStreetNameValidationRules = ({
  brand,
  carrierCode,
  errorMessage,
}: {
  brand: BrandId;
  carrierCode: string;
  errorMessage: {
    invalid: string;
    invalidChar: string;
    invalidComma: string;
    invalidHyphen: string;
    invalidSpace: string;
  };
}): Array<FormValidationRules> => {
  if (brand === 'singsaver') {
    return [
      {
        pattern: PATTERN_NO_PREFIX_NUMBERS,
        errorMessage: errorMessage.invalid,
      },
      {
        pattern: PATTERN_STR_NO_DOUBLE_SPACE,
        errorMessage: errorMessage.invalid,
      },
    ];
  }

  if (brand === 'moneyhero') {
    if (carrierCode === 'QBE') {
      return [
        {
          pattern: PATTERN_2_TO_30_CHARS_ONLY,
          errorMessage: errorMessage.invalid,
        },
        {
          pattern: PATTERN_ALPHA_NUMERIC_SPACE_COMMA_HYPHEN_ONLY,
          errorMessage: errorMessage.invalidChar,
        },
        {
          pattern: PATTERN_STR_NO_DOUBLE_SPACE_ALL,
          errorMessage: errorMessage.invalidSpace,
        },
        {
          pattern: PATTERN_NO_BLANK_SPACE_BEFORE_AFTER_HYPHEN,
          errorMessage: errorMessage.invalidHyphen,
        },
        {
          pattern: PATTERN_CHAR_BEFORE_AND_SPACE_AFTER_COMMA,
          errorMessage: errorMessage.invalidComma,
        },
        {
          pattern: PATTERN_EQUAL_OR_LESS_THAN_3_CONSECUTIVE_CHAR,
          errorMessage: errorMessage.invalid,
        },
        {
          pattern: PATTERN_NO_PREFIX_NUMBERS,
          errorMessage: errorMessage.invalid,
        },
        {
          pattern: PATTERN_STR_NO_TRAILING_SPACE_MAX_248,
          errorMessage: errorMessage.invalid,
        },
        {
          pattern: PATTERN_ONLY_ALLOW_INITIAL_ALPHANUMERIC,
          errorMessage: errorMessage.invalid,
        },
      ];
    }

    return [
      {
        pattern: PATTERN_1_TO_50_CHARS_ONLY,
        errorMessage: errorMessage.invalid,
      },
      {
        pattern: PATTERN_ALPHA_NUMERIC_SPACE_ONLY,
        errorMessage: errorMessage.invalid,
      },
      {
        pattern: PATTERN_STR_NO_DOUBLE_SPACE,
        errorMessage: errorMessage.invalid,
      },
      {
        pattern: PATTERN_NO_PREFIX_NUMBERS,
        errorMessage: errorMessage.invalid,
      },
      {
        pattern: PATTERN_STR_NO_TRAILING_SPACE_MAX_248,
        errorMessage: errorMessage.invalid,
      },
    ];
  }

  return [];
};

export const getBuildingNameValidationRules = ({
  brand,
  carrierCode,
  errorMessage,
}: {
  brand: BrandId;
  carrierCode: string;
  errorMessage: {
    invalidChar: string;
    invalidCharWithCustomValidCharacters: string;
    invalidComma: string;
    invalidHyphen: string;
    invalid: string;
    invalidSpace: string;
    maxLengthLessThanForty: string;
  };
}): Array<FormValidationRules> => {
  if (brand === 'singsaver') {
    return [
      {
        pattern: PATTERN_STR_NO_EXCESS_SPACE_MAX_248,
        errorMessage: errorMessage.invalid,
      },
      ...(carrierCode === 'MSIG'
        ? [
            {
              pattern: PATTERN_MSIG_BUILDING_NAME,
              errorMessage:
                errorMessage.invalidCharWithCustomValidCharacters.replace(
                  '${validCharacters}',
                  `-/'.(),+_=&@#$%*:;?`,
                ),
            },
            {
              pattern: PATTERN_STR_NO_EXCESS_SPACE_MAX_40,
              errorMessage: errorMessage.maxLengthLessThanForty,
            },
          ]
        : []),
    ];
  }

  if (brand === 'moneyhero') {
    if (carrierCode === 'QBE') {
      return [
        {
          pattern: PATTERN_2_TO_30_CHARS_ONLY,
          errorMessage: errorMessage.invalid,
        },
        {
          pattern: PATTERN_ALPHA_NUMERIC_SPACE_COMMA_HYPHEN_ONLY,
          errorMessage: errorMessage.invalidChar,
        },
        {
          pattern: PATTERN_STR_NO_DOUBLE_SPACE_ALL,
          errorMessage: errorMessage.invalidSpace,
        },
        {
          pattern: PATTERN_NO_BLANK_SPACE_BEFORE_AFTER_HYPHEN,
          errorMessage: errorMessage.invalidHyphen,
        },
        {
          pattern: PATTERN_CHAR_BEFORE_AND_SPACE_AFTER_COMMA,
          errorMessage: errorMessage.invalidComma,
        },
        {
          pattern: PATTERN_EQUAL_OR_LESS_THAN_3_CONSECUTIVE_CHAR,
          errorMessage: errorMessage.invalid,
        },
        {
          pattern: PATTERN_NO_PREFIX_NUMBERS,
          errorMessage: errorMessage.invalid,
        },
        {
          pattern: PATTERN_STR_NO_TRAILING_SPACE_MAX_248,
          errorMessage: errorMessage.invalid,
        },
        {
          pattern: PATTERN_ONLY_ALLOW_INITIAL_ALPHANUMERIC,
          errorMessage: errorMessage.invalid,
        },
      ];
    }

    return [
      {
        pattern: PATTERN_1_TO_50_CHARS_ONLY,
        errorMessage: errorMessage.invalid,
      },
      {
        pattern: PATTERN_ALPHA_NUMERIC_HYPHEN_FWDSLASH_SPACE_ONLY,
        errorMessage: errorMessage.invalid,
      },
      {
        pattern: PATTERN_STR_NO_DOUBLE_SPACE_HYPHEN_FWDSLASH_SPACE_ONLY,
        errorMessage: errorMessage.invalid,
      },
      {
        pattern: PATTERN_NO_PREFIX_NUMBERS,
        errorMessage: errorMessage.invalid,
      },
    ];
  }

  return [];
};

export const getPrimaryAddressSingleLineValidationRules = ({
  errorMessage,
}: {
  errorMessage: {
    invalid: string;
  };
}): Array<FormValidationRules> => {
  return [
    {
      pattern: PATTERN_1_TO_105_CHARS_ONLY,
      errorMessage: errorMessage.invalid,
    },
    {
      pattern: PATTERN_ALPHA_NUMERIC_HYPHEN_FWDSLASH_SPACE_ONLY,
      errorMessage: errorMessage.invalid,
    },
  ];
};

/**
 * Latin-script-only free text: the same character set as
 * `getPrimaryAddressSingleLineValidationRules` (letters, digits, space and the
 * usual address punctuation) without its 1–105 character rule, for fields
 * whose length is capped by the input's `maxLength` or not capped at all.
 *
 * The point is to keep CJK out of values that are printed onto carrier
 * documents — Generali's policy PDF renders non-Latin characters as blank.
 */
export const getEnglishOnlyValidationRules = ({
  errorMessage,
}: {
  errorMessage: {
    invalid: string;
  };
}): Array<FormValidationRules> => {
  return [
    {
      pattern: PATTERN_ALPHA_NUMERIC_HYPHEN_FWDSLASH_SPACE_ONLY,
      errorMessage: errorMessage.invalid,
    },
  ];
};

export const getPassportValidationRules = ({
  errorMessage,
}: {
  errorMessage: {
    mustBeAtLeast6Chars: string;
    specialChar: string;
  };
}): Array<FormValidationRules> => {
  return [
    {
      pattern: PATTERN_MUST_BE_AT_LEAST_6_CHARS,
      errorMessage: errorMessage.mustBeAtLeast6Chars,
    },
    {
      pattern: PATTERN_PASSPORT,
      errorMessage: errorMessage.specialChar,
    },
  ];
};

export const getNricOrFinValidationRules = ({
  errorMessage,
}: {
  errorMessage: {
    invalid: string;
  };
}): Array<FormValidationRules> => {
  return [
    {
      validateFn: (nricOrFin?: string) => {
        if (!nricOrFin) {
          return false;
        }

        const value = nricOrFin?.toUpperCase();
        const isRegexValid = value && PATTERN_SG_ID.test(value);
        if (isRegexValid) {
          const icArray: string[] = value.split('');
          const firstchar = icArray[0] as 'S' | 'T' | 'F' | 'G' | 'M';
          const lastChar = icArray[icArray.length - 1];
          const weights = [2, 7, 6, 5, 4, 3, 2];
          let weight = 0;
          for (let i = 1; i < 8; i++) {
            weight += parseInt(icArray[i]) * weights[i - 1];
          }
          const offset =
            firstchar === 'T' || firstchar === 'G'
              ? 4
              : firstchar === 'M'
                ? 3
                : 0;
          let index = (offset + weight) % 11;
          if (firstchar === 'M') index = 10 - index;
          const checksums = {
            S: ['J', 'Z', 'I', 'H', 'G', 'F', 'E', 'D', 'C', 'B', 'A'],
            T: ['J', 'Z', 'I', 'H', 'G', 'F', 'E', 'D', 'C', 'B', 'A'],
            F: ['X', 'W', 'U', 'T', 'R', 'Q', 'P', 'N', 'M', 'L', 'K'],
            G: ['X', 'W', 'U', 'T', 'R', 'Q', 'P', 'N', 'M', 'L', 'K'],
            M: ['K', 'L', 'J', 'N', 'P', 'Q', 'R', 'T', 'U', 'W', 'X'],
          };
          return checksums[firstchar][index] === lastChar;
        }
        return false;
      },
      errorMessage: errorMessage.invalid,
    },
  ];
};

export const getNricValidationRules = ({
  errorMessage,
}: {
  errorMessage: {
    invalid: string;
    invalidNricFormat?: string;
    nricMustBe9Chars?: string;
  };
}): Array<FormValidationRules> => {
  return [
    {
      pattern: PATTERN_MUST_BE_9_CHARS,
      errorMessage: errorMessage?.nricMustBe9Chars || errorMessage.invalid,
    },
    {
      pattern: PATTERN_NRIC,
      errorMessage: errorMessage?.invalidNricFormat || errorMessage.invalid,
    },
    ...getNricOrFinValidationRules({
      errorMessage: {
        invalid: errorMessage?.invalidNricFormat || errorMessage.invalid,
      },
    }),
  ];
};

export const getFinValidationRules = ({
  errorMessage,
}: {
  errorMessage: {
    invalid: string;
    invalidFinFormat?: string;
    finMustBe9Chars?: string;
  };
}): Array<FormValidationRules> => {
  return [
    {
      pattern: PATTERN_MUST_BE_9_CHARS,
      errorMessage: errorMessage?.finMustBe9Chars || errorMessage.invalid,
    },
    {
      pattern: PATTERN_FIN,
      errorMessage: errorMessage?.invalidFinFormat || errorMessage.invalid,
    },
    ...getNricOrFinValidationRules({
      errorMessage: {
        invalid: errorMessage?.invalidFinFormat || errorMessage.invalid,
      },
    }),
  ];
};

export const getHKIDValidationRules = ({
  errorMessage,
}: {
  errorMessage: {
    invalid: string;
  };
}): Array<FormValidationRules> => {
  return [
    {
      pattern: PATTERN_ALPHA_NUMERIC,
      errorMessage: errorMessage.invalid,
    },
    {
      validateFn: (hkid?: string) => {
        if (!hkid || hkid.length > 15) {
          return false;
        }

        const match = (hkid || '').match(PATTERN_HK_ID);
        if (!match) {
          return false;
        }

        const [_, letters, digits, checkDigit1, checkDigit2] = match;
        const checkDigit = checkDigit2 || checkDigit1;
        const values = (letters + digits).split('').map((s) => parseInt(s, 36));
        if (letters.length < 2) {
          values.unshift(36);
        }
        values.push(0, 0);
        values.reverse();
        const checksum = values.reduce((acc, cur, idx) => acc + cur * idx);
        const computedCheckDigit = ((11 - (checksum % 11)) % 11).toString(16);
        return checkDigit.toUpperCase() === computedCheckDigit.toUpperCase();
      },
      errorMessage: errorMessage.invalid,
    },
  ];
};

export const getFloorValidationRules = ({
  errorMessage,
}: {
  errorMessage: {
    invalid: string;
  };
}): Array<FormValidationRules> => {
  return [
    {
      pattern: PATTERN_1_TO_18_CHARS_ONLY,
      errorMessage: errorMessage.invalid,
    },
    {
      pattern: PATTERN_ALPHA_NUMERIC_HYPHEN_FWDSLASH_ONLY,
      errorMessage: errorMessage.invalid,
    },
  ];
};
